// @ts-nocheck
/* eslint-disable */
import { useSortable } from '@dnd-kit/sortable';
import { useState, useCallback, useEffect, useRef } from 'react';
import { PositionLeg } from 'types/optionsCalculator';
import { formatStrikeIndicatorExpiration } from 'util/optionsCalculator';
import { CSS } from '@dnd-kit/utilities';
import {
  Popper,
  Paper,
  Box,
  Button,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Typography,
  MenuItem,
  useTheme,
  alpha,
  ClickAwayListener,
  Stack,
  Checkbox,
  ListItemText,
  Menu,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import debounce from 'lodash/debounce';
import { getExpirationLabel, convertToDaysTillExpiry } from 'util/iVol';
import { dayjs, formatAsCurrency } from 'util/shared';
import { negativeTrendColorState, positiveTrendColorState } from 'states';
import { useRecoilValue } from 'recoil';
import { SGTooltip } from 'components/core';
import BuySellBtn from '../positionManagement/BuySellBtn';

// Strike indicator component for draggable legs
const StrikeIndicator = ({
  leg,
  placement,
  xScale,
  height,
  positiveColor,
  negativeColor,
  stackIndex = 0,
  isFirstInStack = false,
  style,
  onLegChange,
  onLegDelete,
  disabledExpirationOptions,
  expirationOptions,
  legGreeks,
}: {
  leg: PositionLeg;
  placement: 'above' | 'below';
  xScale: d3.ScaleLinear<number, number>;
  height: number;
  positiveColor: string;
  negativeColor: string;
  stackIndex?: number;
  isFirstInStack?: boolean;
  style?: React.CSSProperties;
  onLegChange?: (
    legId: number,
    updates: Partial<PositionLeg>,
    shouldUseDefaultPrice: boolean,
  ) => void;
  onLegDelete?: (legId: number) => void;
  expirationOptions: number[];
  disabledExpirationOptions: number[];
  legGreeks?: any;
}) => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const [popperOpen, setPopperOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const serverPosColor = useRecoilValue(positiveTrendColorState);
  const serverNegColor = useRecoilValue(negativeTrendColorState);
  const [expirySelectorOpen, setExpirySelectorOpen] = useState(false);
  const [expirySelectAnchorEl, setExpirySelectAnchorEl] =
    useState<HTMLElement | null>(null);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setExpirySelectAnchorEl(event.currentTarget);
    setExpirySelectorOpen(true);
  };

  const handleClose = () => {
    setExpirySelectorOpen(false);
    setExpirySelectAnchorEl(null);
  };

  // Local states for editing
  const [localQuantity, setLocalQuantity] = useState<number | null>(
    Math.abs(leg.quantity),
  );
  const [localPrice, setLocalPrice] = useState<number | null>(leg.price_paid);
  const [localExpiration, setLocalExpiration] = useState<number>(leg.expiry);

  const indicatorRef = useRef<HTMLDivElement | null>(null);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: leg.leg_id,
    data: { type: 'leg', strike: leg.strike, leg },
  });

  // Update local states when props change
  useEffect(() => {
    setLocalPrice(leg.price_paid);
    setLocalQuantity(Math.abs(leg.quantity));
    setLocalExpiration(leg.expiry);
  }, [leg.price_paid, leg.quantity, leg.expiry]);

  // Debounced update handler
  const debouncedEditLeg = useCallback(
    debounce(
      (
        legId: number,
        updatedLeg: Partial<PositionLeg>,
        shouldUseDefaultPrice?: boolean,
      ) => {
        if (onLegChange) {
          onLegChange(legId, updatedLeg, shouldUseDefaultPrice || false);
        }
      },
      300,
    ),
    [onLegChange],
  );

  const centerX = xScale(leg.strike);
  const displayText = `${leg.strike}${leg.option_type}`;
  const width = displayText.length * 6 + 16;

  const isSecondary = stackIndex > 0;
  const stackSpacing = 24;
  const stackOffset =
    stackIndex * (placement === 'above' ? -stackSpacing : stackSpacing);

  const opacity = isDragging ? 0.5 : 1;
  const zIndexBase = isDragging ? 10 : isHovered ? 5 : 3;
  const zIndex = zIndexBase - stackIndex;

  const combinedStyle: React.CSSProperties = {
    transition: isDragging ? 'none' : isDragging ? 'none' : transition,
    transform: isDragging ? CSS.Transform.toString(transform) : 'none',
    opacity,
    position: 'absolute' as const,
    left: `${centerX - width / 2}px`,
    top:
      placement === 'above'
        ? `${height - 30 + stackOffset}px`
        : `${height + 8 + stackOffset}px`,
    cursor: isDragging ? 'grabbing' : isHovered ? 'grab' : 'pointer',
    zIndex,
    pointerEvents: 'auto' as const,
    ...style,
  };

  const isBuy = leg.quantity >= 0;
  const baseColor = isBuy ? positiveColor : negativeColor;
  const backgroundColor = baseColor;

  // Handle click to open popper - only if not dragging
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    // Don't open popper if we're dragging or have started a drag
    if (isDragging) {
      return;
    }

    // Stop propagation to prevent parent handlers
    event.stopPropagation();
    event.preventDefault();

    setAnchorEl(event.currentTarget);
    setPopperOpen(true);
  };

  const handleClosePopper = () => {
    setPopperOpen(false);
    setAnchorEl(null);
  };

  // Handle click away - but ignore clicks within the popper
  const handleClickAway = () => {
    handleClosePopper();
  };

  // Handle mouse down - track drag start but don't prevent it
  const handleMouseDown = (event: React.MouseEvent<HTMLElement>) => {
    // Close popper if it's open
    event.stopPropagation();
    if (popperOpen) {
      handleClosePopper();
    }

    // Call the sortable mouse down handler
    if (listeners?.onMouseDown) {
      listeners.onMouseDown(event);
    }
  };

  // Update handlers
  const handleTypeChange = (newType: 'C' | 'P') => {
    if (onLegChange) {
      onLegChange(leg.leg_id, { option_type: newType }, true);
    }
  };

  const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value === '') {
      setLocalQuantity(null);
    } else {
      const newQuantity = parseInt(value);
      if (!isNaN(newQuantity)) {
        setLocalQuantity(newQuantity);

        // Maintain sign of the leg quantity
        const quantity = newQuantity * (isBuy ? 1 : -1);
        debouncedEditLeg(leg.leg_id, { quantity }, true);
      }
    }
  };

  const handlePriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value === '') {
      setLocalPrice(null);
    } else {
      const newPrice = parseFloat(value);
      if (!isNaN(newPrice)) {
        setLocalPrice(newPrice);
        debouncedEditLeg(leg.leg_id, { price_paid: newPrice });
      }
    }
  };

  const handleExpirationChange = (utcTime: number) => {
    setLocalExpiration(utcTime);
    debouncedEditLeg(leg.leg_id, { expiry: utcTime }, true);
    handleClose();
  };

  const handleBuySellToggle = () => {
    const newQuantity = -leg.quantity;
    setLocalQuantity(Math.abs(newQuantity));
    onLegChange?.(leg.leg_id, { quantity: newQuantity }, true);
  };

  const handleResetPrice = () => {
    if (legGreeks?.price) {
      const newPrice = Math.round(legGreeks.price * 1000) / 1000;
      setLocalPrice(newPrice);
      debouncedEditLeg(leg.leg_id, { price_paid: newPrice });
    }
  };

  const handleDeleteLeg = () => {
    onLegDelete?.(leg.leg_id);
    handleClosePopper();
  };

  return (
    <>
      <div
        ref={(node: HTMLDivElement | null) => {
          setNodeRef(node);
          if (node) {
            indicatorRef.current = node;
          }
        }}
        style={combinedStyle}
        {...attributes}
        {...listeners}
        onMouseDown={handleMouseDown}
        onMouseUp={handleClick}
        onMouseEnter={() => !isDragging && setIsHovered(true)}
        onMouseLeave={() => !isDragging && setIsHovered(false)}
        onClick={handleClick}
      >
        <div
          style={{
            position: 'relative',
            backgroundColor,
            color: theme.palette.getContrastText(baseColor),
            padding: '4px 8px',
            borderRadius: '3px',
            fontSize: '0.7rem',
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
            boxShadow: isHovered ? theme.shadows[3] : theme.shadows[2],
            border: isHovered ? `1px solid ${baseColor}` : 'none',
            transform:
              isHovered && !isDragging && !isDragging
                ? 'scale(1.05)'
                : 'scale(1)',
            transition:
              isDragging || isDragging ? 'none' : 'all 0.2s ease-in-out',
            // Add visual indicator that it's clickable
            outline: popperOpen
              ? `2px solid ${theme.palette.primary.main}`
              : 'none',
          }}
        >
          {/* Arrow pointing to strike - only show for primary item */}
          {!isSecondary && (
            <div
              style={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                width: 0,
                height: 0,
                borderLeft: '5px solid transparent',
                borderRight: '5px solid transparent',
                ...(placement === 'above'
                  ? {
                      bottom: '-5px',
                      borderTop: `5px solid ${backgroundColor}`,
                    }
                  : {
                      top: '-5px',
                      borderBottom: `5px solid ${backgroundColor}`,
                    }),
              }}
            />
          )}

          {/* Expiration tag - show on secondary if available, otherwise on primary */}
          {leg.expiry && (isSecondary || isFirstInStack) && (
            <div
              style={{
                position: 'absolute',
                ...(placement === 'above'
                  ? {
                      top: -8,
                      right: -4,
                    }
                  : {
                      bottom: -8,
                      right: -4,
                    }),
                backgroundColor: theme.palette.info.main,
                color: theme.palette.info.contrastText,
                fontSize: '0.5rem',
                fontWeight: 'bold',
                padding: '1px 3px',
                borderRadius: '3px',
                lineHeight: 1,
                minWidth: '16px',
                textAlign: 'center',
                boxShadow: theme.shadows[1],
                border: `1px solid ${theme.palette.background.paper}`,
              }}
            >
              {formatStrikeIndicatorExpiration(leg.expiry)}
            </div>
          )}

          {displayText}
        </div>
      </div>

      {/* Popper for editing */}
      <Popper
        open={popperOpen}
        anchorEl={anchorEl}
        placement="top"
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 8],
            },
          },
          {
            name: 'preventOverflow',
            options: {
              boundary: 'viewport',
              padding: 8,
            },
          },
        ]}
        sx={{
          zIndex: 1000,
        }}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper
            elevation={8}
            sx={{
              p: 0,
              minWidth: 320,
              maxWidth: 380,
              backgroundColor: theme.palette.background.default,
              border: `2px solid ${alpha(theme.palette.primary.main, 0.4)}`,
              borderRadius: 2,
              boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`,
              overflow: 'hidden',
            }}
          >
            {/* Header with gradient background */}
            <Box
              sx={{
                background: `linear-gradient(135deg, ${alpha(
                  isBuy ? positiveColor : negativeColor,
                  0.1,
                )} 0%, ${alpha(
                  isBuy ? positiveColor : negativeColor,
                  0.05,
                )} 100%)`,
                p: 2,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={3}>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.text.primary,
                      fontSize: '1.1rem',
                    }}
                  >
                    {formatAsCurrency(leg.strike)} {leg.option_type}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: '0.75rem',
                    }}
                  >
                    {leg.expiry && getExpirationLabel(leg.expiry.toString())}
                  </Typography>
                </Box>
                <BuySellBtn onClick={handleBuySellToggle} isBuy={isBuy} />
                <SGTooltip title="Remove Leg">
                  <IconButton
                    size="small"
                    onClick={handleDeleteLeg}
                    sx={{
                      color: theme.palette.error.main,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.error.main, 0.1),
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </SGTooltip>
              </Stack>
            </Box>

            {/* Content */}
            <Stack sx={{ p: 3, gap: 4 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  width: '100%',
                }}
              >
                <FormControl size="small" sx={{ width: '100%' }}>
                  <InputLabel id="quantity-input-label">Quantity</InputLabel>
                  <OutlinedInput
                    id="quantity-input"
                    value={localQuantity}
                    onChange={handleQuantityChange}
                    endAdornment={
                      <InputAdornment position="end">x</InputAdornment>
                    }
                    label="Quantity"
                    type="number"
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.primary.main, 0.5),
                      },
                      '& .MuiOutlinedInput-input': {
                        fontSize: '12px',
                        MozAppearance: 'textfield',
                      },
                      '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
                        {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                      svg: {
                        color: alpha(theme.palette.primary.main, 0.5),
                      },
                    }}
                  />
                </FormControl>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    handleTypeChange(leg.option_type === 'C' ? 'P' : 'C')
                  }
                  color={leg.option_type === 'C' ? 'success' : 'error'}
                  sx={{
                    width: '100%',
                  }}
                >
                  {leg.option_type === 'C' ? 'CALL' : 'PUT'}
                </Button>
              </Box>

              {/* Price */}
              <Box sx={{ mb: 2 }}>
                <FormControl size="small" sx={{ width: '100%' }}>
                  <InputLabel id="price-label">Price</InputLabel>
                  <OutlinedInput
                    id="price-input"
                    value={localPrice}
                    onChange={handlePriceChange}
                    type="number"
                    inputProps={{ step: 0.01 }}
                    startAdornment={
                      <InputAdornment position="start">$</InputAdornment>
                    }
                    endAdornment={
                      legGreeks?.price && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={handleResetPrice}
                            sx={{
                              padding: '2px',
                            }}
                            color="primary"
                          >
                            <RestartAltIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      )
                    }
                    label="Price"
                    sx={{
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.35),
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                      '& input': {
                        MozAppearance: 'textfield',
                      },
                      '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
                        {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                    }}
                  />
                </FormControl>
              </Box>

              {/* Expiration */}
              {expirationOptions.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Button
                    id="expiry-date-select-button"
                    aria-controls={
                      expirySelectorOpen ? 'expiry-date-menu' : undefined
                    }
                    aria-haspopup="true"
                    aria-expanded={expirySelectorOpen ? 'true' : undefined}
                    onClick={handleOpen}
                    variant="outlined"
                    fullWidth
                    sx={{
                      justifyContent: 'flex-start',
                      textTransform: 'none',
                      borderRadius: 1,
                      padding: '12px 14px',
                      height: '36px',
                      textAlign: 'left',
                      fontSize: { xs: 11, md: 13 },
                      position: 'relative',
                      color: theme.palette.text.primary,
                      '&::before': {
                        content: '"Expiration"',
                        position: 'absolute',
                        top: '-8px',
                        left: '12px',
                        backgroundColor: theme.palette.background.paper,
                        padding: '0 4px',
                        fontSize: '10px',
                        lineHeight: 1,
                      },
                    }}
                  >
                    {localExpiration
                      ? `${getExpirationLabel(localExpiration.toString())} (${convertToDaysTillExpiry(
                          dayjs().valueOf(),
                          localExpiration,
                        )} DTE)`
                      : 'Select Expiration'}
                  </Button>
                  <Menu
                    id="expiry-date-menu"
                    anchorEl={expirySelectAnchorEl}
                    open={expirySelectorOpen}
                    onClose={handleClose}
                    slotProps={{
                      root: {
                        'aria-labelledby': 'expiry-date-select-button',
                      },
                      paper: {
                        sx: {
                          width: 300,
                          maxHeight: '250px',
                        },
                      },
                    }}
                  >
                    {expirationOptions.map((utcTime: number) => {
                      return (
                        <MenuItem
                          key={utcTime}
                          onClick={() => handleExpirationChange(utcTime)}
                          sx={{
                            padding: '10px',
                          }}
                          disabled={disabledExpirationOptions.includes(utcTime)}
                        >
                          <Checkbox
                            checked={leg.expiry === utcTime}
                            style={{
                              padding: '0px 10px 0px 0px',
                            }}
                          />
                          <ListItemText
                            primary={`${getExpirationLabel(utcTime.toString())}`}
                            secondary={`${convertToDaysTillExpiry(
                              dayjs().valueOf(),
                              utcTime,
                            )} DTE`}
                            primaryTypographyProps={{
                              fontSize: '13px',
                            }}
                          />
                        </MenuItem>
                      );
                    })}
                  </Menu>
                </Box>
              )}
            </Stack>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default StrikeIndicator;
